import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Settings, User, Bell } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

function GroupSettings({ group, isOpen, onClose, onUpdate }) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, {
        name,
        description
      });
      
      onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to update group: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Group Settings</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
              {error}
            </div>
          )}

          {/* Basic Info Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-lg font-medium text-gray-700">
              <Settings className="h-5 w-5" />
              <h3>Basic Information</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Group Name
                </label>
                <Input 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 p-2 w-full border rounded-md" // Updated to match textarea style
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-md h-24 resize-none"
                  placeholder="Add a description for your group"
                />
              </div>
            </div>
          </div>

          {/* Member Settings - Placeholder for future implementation */}
          <div className="space-y-4 opacity-50">
            <div className="flex items-center space-x-2 text-lg font-medium text-gray-700">
              <User className="h-5 w-5" />
              <h3>Member Settings</h3>
            </div>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-300" disabled />
                <span className="text-sm text-gray-600">Allow members to invite others</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-300" disabled />
                <span className="text-sm text-gray-600">Members can see other members' status</span>
              </label>
            </div>
          </div>

          {/* Notifications - Placeholder for future implementation */}
          <div className="space-y-4 opacity-50">
            <div className="flex items-center space-x-2 text-lg font-medium text-gray-700">
              <Bell className="h-5 w-5" />
              <h3>Notifications</h3>
            </div>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-300" disabled />
                <span className="text-sm text-gray-600">Member status changes</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-300" disabled />
                <span className="text-sm text-gray-600">New member joins</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-2 justify-end pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default GroupSettings;